// Angular Imports
import { Component, inject, OnInit } from "@angular/core";
import { Router, RouterLink, RouterOutlet } from "@angular/router";
import { FormsModule } from "@angular/forms";

// Ant Imports
import { NzColDirective, NzRowDirective } from "ng-zorro-antd/grid";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzLayoutModule } from "ng-zorro-antd/layout";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzMenuModule } from "ng-zorro-antd/menu";

// Services
import { CookieBridgeService } from "#services/cookie-service/cookie-bridge.service";
import { UpdateWorkerService } from "#services/update-worker/update-worker.service";
import { FileManagerService } from "#services/file-manager/file-manager.service";
import { TranslationService } from "#services/translation/translation.service";
import { NavigationService } from "#services/navigation/navigation.service";
import { HotkeysService } from "@ngneat/hotkeys";
import { LoaderService } from "#services/loader/loader.service";
import { ThemeService } from "#services/theme/theme.service";
import { EnvService } from "#services/env/env.service";

// Components
import { FileManagerModalComponent } from "#components/file-manager/file-manager-modal/file-manager-modal.component";
import { NavigationComponent } from "#components/navigation/navigation.component";
import { LoaderComponent } from "#components/loader/loader.component";
import { GotoComponent } from "#components/goto/goto.component";
import { TranslationLangs } from "#models/translations";
import { DatePipe } from "@angular/common";

@Component({
    selector: "app-root",
    imports: [
        RouterLink,
        RouterOutlet,
        NzIconModule,
        NzLayoutModule,
        NzMenuModule,
        NzButtonModule,
        NzRowDirective,
        NzColDirective,
        NzSelectModule,
        FormsModule,
        NavigationComponent,
        LoaderComponent,
        GotoComponent,
        FileManagerModalComponent,
    ],
    providers: [DatePipe],
    templateUrl: "./app.component.html",
    styleUrl: "./app.component.scss",
    standalone: true,
})
export class AppComponent implements OnInit {
    title = "ultra-promo";
    isCollapsed!: boolean;
    currentLanguage: string;
    translation: object | null = null;

    private router = inject(Router);
    fileManagerService = inject(FileManagerService);

    translationService = inject(TranslationService);
    loaderService = inject(LoaderService);
    private readonly themeService = inject(ThemeService);
    private readonly cookieService = inject(CookieBridgeService);
    private readonly hotKeys = inject(HotkeysService);
    private readonly navigationService = inject(NavigationService);
    private readonly envService = inject(EnvService);
    private updateService = inject(UpdateWorkerService);

    constructor() {
        this.navigationService.init();

        this.currentLanguage = this.translationService.currentLanguage;

        this.hotKeys
            .addShortcut({ keys: "shift.t", preventDefault: true })
            .subscribe(async () => await this.toggleTheme());

        this.hotKeys
            .addShortcut({ keys: "shift.m", preventDefault: true })
            .subscribe(() => this.collapsedChanged());

        this.hotKeys
            .addShortcut({ keys: "shift.p", preventDefault: true })
            .subscribe(() => this.router.navigate(["/promos"]));

        this.hotKeys
            .addShortcut({ keys: "shift.c", preventDefault: true })
            .subscribe(() => this.router.navigate(["/promo-codes"]));

        this.hotKeys
            .addShortcut({ keys: "shift.s", preventDefault: true })
            .subscribe(() => this.router.navigate(["/config"]));

        this.isCollapsed = JSON.parse(
            this.cookieService.get("app.navigation.isCollapsed") || "true"
        );

        this.themeService.themeName.set(this.cookieService.get("theme") || "default");

        this.updateService.init();
    }

    async toggleTheme(): Promise<void> {
        await this.themeService.toggleTheme();
        this.cookieService.set("theme", this.themeService.themeName());
    }

    collapsedChanged() {
        this.isCollapsed = !this.isCollapsed;
        this.cookieService.set("app.navigation.isCollapsed", JSON.stringify(this.isCollapsed));
    }

    getThemeName() {
        return this.themeService.themeName();
    }

    async ngOnInit() {
        await this.themeService.loadTheme();
        this.translation = await this.translationService.loadTranslations();
        this.envService.init();
    }

    closeFileManager() {
        this.fileManagerService.hide();
    }

    changeLanguage(lang: TranslationLangs[number]) {
        this.translationService.setLanguage(lang);
    }
}
